import { SubtypeDimension, SubtypeMetric } from '@kentik/ui-shared/dictionary/v2/conversion.types';
import { DictionaryRuntimeV2 } from '@kentik/ui-shared/dictionary/types';
import {
  allRegexOperators,
  inetOperators,
  intOperators,
  OperatorRecord,
  regexOperators,
  stringOperators
} from '@kentik/ui-shared/dictionary/operators';
import { MeasurementDimension } from '@kentik/ui-shared/query/query.types';
import { getAllDimensionKeys } from '@kentik/ui-shared/dictionary/v2/compilers/util';
import { DeepReadonly } from '@kentik/ui-shared/util/asStatic';
import { operators } from '@kentik/ui-shared/dictionary/content/operators';
import { CustomDimensionOutgoing } from '@kentik/ui-shared/types/customDimensions.types';
import { uint32Operators } from '@kentik/ui-shared/dictionary/v2/compilers/customDimensionConstants';
import { AppProtocols } from '@kentik/ui-shared/dictionary/v2/processors/appProtocols';

const operatorTypes = {
  INT: intOperators,
  INET: inetOperators,
  STR: stringOperators
};

/**
 * Maps MeasurementDimension.Type values (e.g. 'string', 'int', 'inet') to the appropriate
 * OperatorRecord. String-typed dimensions receive full string + regex operators.
 * Returns undefined when the type is unrecognised or absent.
 */
const measurementDimensionTypeOperators: Record<string, OperatorRecord> = {
  string: { ...stringOperators, ...regexOperators },
  int: intOperators,
  uint32: intOperators,
  inet: inetOperators
};

export function getOperatorsForMeasurementDimension(dim: MeasurementDimension | undefined): OperatorRecord | undefined {
  if (!dim?.Type) {
    return undefined;
  }
  return measurementDimensionTypeOperators[dim.Type.toLowerCase()];
}

export const getOperatorsFromDimensions = (
  dimensions: DeepReadonly<SubtypeDimension[]>
): DictionaryRuntimeV2['operators'] =>
  Object.fromEntries(
    dimensions
      .filter(({ dataType }) => dataType)
      .flatMap((dim) =>
        getAllDimensionKeys(dim).map((k) => [k, operatorTypes[dim.dataType as keyof typeof operatorTypes]])
      )
  );

export const getOperatorsFromMetrics = (metrics: DeepReadonly<SubtypeMetric[]>): DictionaryRuntimeV2['operators'] =>
  Object.fromEntries(
    metrics
      .filter(({ dataType }) => dataType)
      .map(({ key, dataType }) => [key, operatorTypes[dataType as keyof typeof operatorTypes]])
  );

export const getOperatorsFromAppProtocols = ({ all }: AppProtocols): DictionaryRuntimeV2['operators'] =>
  Object.fromEntries(
    all.map(
      ({
        column: {
          custom_column,
          metadata: { allow_regex_filtering }
        },
        colKey
      }) => {
        if (custom_column.startsWith('INT')) {
          return [colKey, intOperators];
        }
        if (custom_column.startsWith('INET_')) {
          return [colKey, inetOperators];
        }
        if (custom_column.startsWith('STR')) {
          return [colKey, allow_regex_filtering ? { ...stringOperators, ...allRegexOperators } : stringOperators];
        }
        if (operators[custom_column]) {
          return [colKey, operators[custom_column]];
        }
        return [];
      }
    )
  );

export const getOperatorsFromCustomDimensions = (columns: CustomDimensionOutgoing[]) =>
  Object.fromEntries(
    columns.map(({ col_name, col_type }) => [col_name, col_type === 'uint32' ? uint32Operators : stringOperators])
  );
